import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'subject',
    'message',
    'emailField',
    'previewButton',
    'submitButton',
    'recipientError',
  ];
  static values = {
    messageTemplate: String,
    defaultSubject: String,
    designerName: String,
    shareableType: String,
    sendEmail: { type: Boolean, default: true },
    labels: Object,
    draft: { type: Boolean, default: false },
    projectClientIds: Array,
    requireClient: { type: Boolean, default: false },
  };

  #subjectEdited = false;
  #messageEdited = false;
  #initialConnect = true;
  #hasClients = false;
  #clients = [];
  #selectionChanged = false;

  connect() {
    this.subjectTarget.addEventListener('input', this.#markSubjectEdited);
    this.#applyEmailVisibility();
    this.#applyPreviewButton();
    this.#applySubmitLabel();
    queueMicrotask(() => this.#notifyPresets());
    this.#initialConnect = false;
  }

  disconnect() {
    this.subjectTarget.removeEventListener('input', this.#markSubjectEdited);
    this.messageTarget.removeEventListener('input', this.#markMessageEdited);
  }

  #markSubjectEdited = () => {
    this.#subjectEdited = true;
  };

  #markMessageEdited = () => {
    this.#messageEdited = true;
    this.#notifyPresets();
  };

  toggleEmail(event) {
    this.sendEmailValue = event.target.checked;
    this.#applyEmailVisibility();
    this.#applyPreviewButton();
    this.#applySubmitLabel();
    if (!this.sendEmailValue) {
      this.#hideRecipientError();
    }
  }

  validateRecipient(event) {
    if (!this.requireClientValue || !this.sendEmailValue || this.#hasClients) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hasRecipientErrorTarget) {
      this.recipientErrorTarget.classList.remove('hidden');
    }
  }

  #hideRecipientError() {
    if (this.hasRecipientErrorTarget) {
      this.recipientErrorTarget.classList.add('hidden');
    }
  }

  #applyPreviewButton() {
    if (!this.hasPreviewButtonTarget) {
      return;
    }
    this.previewButtonTarget.classList.toggle('hidden', !this.sendEmailValue);
    this.previewButtonTarget.disabled = !this.#hasClients;
  }

  #applySubmitLabel() {
    if (!this.hasSubmitButtonTarget || !this.hasLabelsValue) {
      return;
    }
    const outcome = this.#outcome();
    this.submitButtonTarget.textContent = this.labelsValue[outcome] || this.labelsValue.share;
    this.submitButtonTarget.disabled = outcome === 'idle' || this.#unchangedDesigns;
  }

  get #unchangedDesigns() {
    return this.#designs && !this.#selectionChanged && !this.#addsClients;
  }

  selectionChanged({ detail: { changed } }) {
    this.#selectionChanged = changed;
    this.#applySubmitLabel();
  }

  #outcome() {
    if (this.#designs) {
      return this.sendEmailValue ? 'send' : 'save';
    }
    if (this.sendEmailValue) {
      return this.#pending ? 'send' : 'resend';
    }
    return this.#pending ? 'share' : 'idle';
  }

  get #designs() {
    return ['designs', 'single_design'].includes(this.shareableTypeValue);
  }

  get #pending() {
    if (this.shareableTypeValue === 'project') {
      return this.#clients.length === 0 || this.#addsClients;
    }
    return this.draftValue || this.#addsClients;
  }

  get #addsClients() {
    const known = this.projectClientIdsValue.map(String);
    return this.#clients.some((client) => !known.includes(client.id));
  }

  #applyEmailVisibility() {
    this.emailFieldTargets.forEach((el) => {
      el.classList.toggle('hidden', !this.sendEmailValue);
    });
  }

  clientsChanged({ detail: { clients } }) {
    this.#clients = clients;
    this.#hasClients = clients.length > 0;
    this.#applyPreviewButton();
    this.#applySubmitLabel();
    this.dispatch('clientsChanged', { detail: { hasClients: this.#hasClients } });

    if (this.#hasClients) {
      this.#hideRecipientError();
    }

    if (!this.#subjectEdited) {
      this.#updateSubject(clients);
    }

    if (!this.#messageEdited) {
      this.#updateMessage(clients);
    }
  }

  #updateSubject(clients) {
    if (this.shareableTypeValue === 'designs') {
      const designsText = clients.length === 1 ? 'Design' : 'Designs';
      const teamName = this.defaultSubjectValue.split(' from ').pop();
      this.subjectTarget.value = `${designsText} for Review from ${teamName}`;
    }
  }

  #updateMessage(clients) {
    const template = this.messageTemplateValue;
    if (!template) {
      return;
    }
    let clientName = '';

    if (clients.length === 1) {
      clientName = clients[0].name?.split(' ')[0] || '';
    }

    this.messageTarget.value = template
      .replace('{client_name}', clientName)
      .replace(/^Hi ,/, 'Hi,');

    this.#notifyPresets();
  }

  messageTargetConnected(target) {
    target.addEventListener('input', this.#markMessageEdited);
    if (this.#initialConnect) {
      return;
    }
    this.#messageEdited = true;
    this.#notifyPresets();
  }

  #notifyPresets() {
    this.dispatch('messageChanged', { detail: { message: this.messageTarget.value } });
  }
}
